/**
 * TextButton - React (web) implementation.
 *
 * Uses a semantic button while keeping the visual treatment text-only.
 */

import React from 'react';
import { ensureFocusVisibleStyle } from '../../elements/focusStyle.web';
import { colors } from '../../elements/colors';
import { sizing } from '../../elements/sizing';
import { typography } from '../../elements/typography';
import { Spinner } from '../Spinner/Spinner.web';
import type { TextButtonProps } from './TextButton';
import {
  TEXT_BUTTON_HORIZONTAL_PADDING,
  TEXT_BUTTON_MIN_HEIGHT,
  TEXT_BUTTON_MIN_WIDTH,
  TEXT_BUTTON_VERTICAL_PADDING,
  textButtonColorForState,
  textButtonTypographyForType,
} from './TextButton';

export type WebTextButtonSize = 'L' | 'M';

export interface WebTextButtonProps extends TextButtonProps {
  /** Web-only label size for the `Button` type; `L` is the shared look. */
  size?: WebTextButtonSize;
  /** Web-only busy state: shows a spinner in place of the label and blocks presses. */
  loading?: boolean;
  actionId?: string;
  onPress?: () => void;
  'aria-label'?: string;
  /** Disclosure state, for a button that shows and hides a section. */
  'aria-expanded'?: boolean;
  /** Id of the section the button shows and hides. */
  'aria-controls'?: string;
  'data-testid'?: string;
}

const TEXT_BUTTON_FOCUS_STYLE_ID = 'galapagos-text-button-focus-visible';
const TEXT_BUTTON_FOCUS_SELECTOR = '[data-galapagos-text-button="true"]';

// Web-only: the M label size and the spinner that replaces the label while loading.
const WEB_TEXT_BUTTON_M_TYPOGRAPHY = typography.button2;
const WEB_TEXT_BUTTON_SPINNER_SIZE = sizing.icon.sm;
// Web-only: a disabled link (e.g. "Send again" during a countdown) stays readable
// and underlined in the secondary text colour, so it still reads as a link that
// is waiting, not as missing. aria-disabled still marks it disabled.
const WEB_TEXT_BUTTON_DISABLED_LINK_COLOR = colors.text.secondary;

export function TextButton({
  actionId = 'ui.text-button.activate',
  label,
  type = 'Button',
  state = 'Default',
  size = 'L',
  loading = false,
  accessibilityLabel,
  testID,
  onPress,
  'aria-label': ariaLabel,
  'aria-expanded': ariaExpanded,
  'aria-controls': ariaControls,
  'data-testid': dataTestId,
}: WebTextButtonProps) {
  ensureFocusVisibleStyle(TEXT_BUTTON_FOCUS_STYLE_ID, [TEXT_BUTTON_FOCUS_SELECTOR]);
  const [isHovered, setIsHovered] = React.useState(false);
  const isDisabled = state === 'Disabled';
  const isInert = isDisabled || loading;
  const isLink = type === 'Link';
  const fg =
    isLink && isDisabled ? WEB_TEXT_BUTTON_DISABLED_LINK_COLOR : textButtonColorForState[state];
  const font =
    !isLink && size === 'M' ? WEB_TEXT_BUTTON_M_TYPOGRAPHY : textButtonTypographyForType[type];
  const underlined = isLink || (isHovered && !isInert);

  const style: React.CSSProperties = {
    minHeight: TEXT_BUTTON_MIN_HEIGHT,
    minWidth: TEXT_BUTTON_MIN_WIDTH,
    paddingTop: TEXT_BUTTON_VERTICAL_PADDING,
    paddingBottom: TEXT_BUTTON_VERTICAL_PADDING,
    paddingLeft: isLink ? 0 : TEXT_BUTTON_HORIZONTAL_PADDING,
    paddingRight: isLink ? 0 : TEXT_BUTTON_HORIZONTAL_PADDING,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: isLink ? 'flex-start' : 'center',
    border: 'none',
    background: 'transparent',
    color: fg,
    fontFamily: font.fontFamily,
    fontWeight: font.fontWeight,
    fontSize: font.fontSize,
    lineHeight: `${font.lineHeight}px`,
    letterSpacing: font.letterSpacing,
    textAlign: isLink ? 'left' : 'center',
    textDecoration: underlined ? 'underline' : undefined,
    cursor: isInert ? 'not-allowed' : 'pointer',
  };

  return (
    <button
      data-analytics-action={actionId}
      type="button"
      aria-label={ariaLabel ?? accessibilityLabel ?? label}
      aria-disabled={isInert || undefined}
      aria-busy={loading || undefined}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      data-galapagos-text-button="true"
      data-testid={dataTestId ?? testID}
      style={style}
      onPointerEnter={(event) => {
        // Mouse only: a tap fires a pointer-enter with no matching leave.
        if (event.pointerType === 'mouse') setIsHovered(true);
      }}
      onPointerLeave={() => setIsHovered(false)}
      onClick={(event) => {
        if (isInert) {
          event.preventDefault();
          return;
        }
        onPress?.();
      }}
    >
      {loading ? (
        // The hidden label and the spinner share one grid cell, so the button
        // keeps its label's width while the spinner shows.
        <span style={{ display: 'inline-grid', alignItems: 'center', justifyItems: 'center' }}>
          <span style={{ gridArea: '1 / 1', visibility: 'hidden' }}>{label}</span>
          <span style={{ gridArea: '1 / 1', display: 'flex' }}>
            <Spinner
              size={WEB_TEXT_BUTTON_SPINNER_SIZE}
              data-testid={
                (dataTestId ?? testID) ? `${dataTestId ?? testID}-loading-indicator` : undefined
              }
            />
          </span>
        </span>
      ) : (
        label
      )}
    </button>
  );
}
